import { css, styled } from 'styled-components';
import React, { CSSProperties, useCallback } from 'react';
import { Table, TableProps } from 'antd';
import { COLORS, computeRemSize, STYLE } from '@ydistri/theme';

export interface DataTableDivider {
  divider: boolean;
  key: string;
}

const TableWrapper = styled.div<{
  $id: string;
  $showBorder: boolean;
  $hideSelectionColumn?: boolean;
  $height?: CSSProperties['height'];
  $minRowHeight?: CSSProperties['height'];
  $flexGrow?: number;
}>`
  ${props => props.$flexGrow && `flex-grow: ${props.$flexGrow};`}
  ul.ant-table-pagination-left {
    padding: 0.5rem;
    background-color: ${COLORS.GREY_BORDER};

    .ant-pagination-total-text {
      width: calc(50% - ${computeRemSize(200)});
    }
  }

  #${props => props.$id} {
    pre {
      margin: 0;
    }

    .ant-table-body,
    .ant-table-content {
      ${props =>
        props.$height !== undefined
          ? css`
              overflow-y: scroll;
              max-height: ${props.$height};
              ${STYLE.SCROLLBAR}
            `
          : css`
              overflow-y: auto;
            `}
      .selected {
        background-color: ${props => props.theme.item.selected.backgroundColor};
      }
    }

    .highlight-selected {
      background-color: ${props => props.theme.item.hovered.backgroundColor};

      & td:first-child {
        border-left: ${computeRemSize(3)} solid ${COLORS.PRIMARY};
      }
    }

    td,
    div.ant-table-cell {
      ${props =>
        props.$minRowHeight !== undefined &&
        css`
          height: ${props.$minRowHeight};
          min-height: ${props.$minRowHeight};
        `}
    }

    .bold td {
      font-weight: bold;
    }

    tr.ant-table-expanded-row {
      & > td.ant-table-cell {
        padding-right: 0 !important;
      }
    }

    .add-left-border td:first-child {
      border-left: ${computeRemSize(3)} solid transparent;
    }

    td.ant-table-cell:not(.ant-table-cell-scrollbar),
    th.ant-table-cell,
    div.ant-table-cell {
      padding: 0.25rem !important;
      padding-right: 0.75rem !important;
      ${p =>
        !p.$showBorder &&
        css`
          border-bottom: none !important;
        `};
    }

    td.ant-table-selection-column {
      ${props => props.$hideSelectionColumn && 'width: 0; padding: 0 !important;'}
    }

    tr.green:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.green,
    div.ant-table-row.green:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.green {
      background-color: ${COLORS.TABLE_HIGHLIGHT_GREEN};
    }

    tr.blue:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.blue,
    div.ant-table-row.blue:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.blue {
      background-color: ${COLORS.TABLE_HIGHLIGHT_BLUE};
    }

    tr.orange:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.orange,
    div.ant-table-row.orange:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.orange {
      background-color: ${COLORS.TABLE_HIGHLIGHT_ORANGE};
    }

    tr.red:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.red,
    div.ant-table-row.red:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.red {
      background-color: ${COLORS.TABLE_HIGHLIGHT_RED};
    }

    tr.grey:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.grey,
    div.ant-table-row.grey:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.grey {
      background-color: ${COLORS.TABLE_HIGHLIGHT_GREY};
    }

    tr.green:hover td,
    tr.orange:hover td,
    tr.red:hover td,
    div.ant-table-row.green:hover > div.ant-table-cell,
    div.ant-table-row.orange:hover > div.ant-table-cell,
    div.ant-table-row.red:hover > div.ant-table-cell {
      filter: brightness(0.95);
    }

    .ant-table-tbody > tr.ant-table-row.divider > td.ant-table-cell {
      border-bottom: ${computeRemSize(1)} solid #e3e3e3 !important;
    }
  }
`;

export interface DataTableProps<T> extends TableProps<T> {
  id: string;
  showBorder?: boolean;
  showHeader?: boolean;
  hideSelectionColumn?: boolean;
  height?: CSSProperties['height'];
  minRowHeight?: CSSProperties['height'];
  flexGrow?: number;
}

export const DataTable = <T extends object>({
  id,
  showBorder = false,
  showHeader = false,
  hideSelectionColumn = false,
  height,
  minRowHeight,
  flexGrow,
  ...rest
}: DataTableProps<T>): React.ReactElement => {
  const getRowClassName = useCallback((data: T) => {
    if ('divider' in data) {
      return `divider`;
    }
    return '';
  }, []);

  return (
    <TableWrapper
      $id={id}
      $showBorder={showBorder}
      $hideSelectionColumn={hideSelectionColumn}
      $height={height}
      $minRowHeight={minRowHeight}
      $flexGrow={flexGrow}
    >
      <Table<T>
        id={id}
        pagination={false}
        showHeader={showHeader}
        rowClassName={getRowClassName}
        {...rest}
      />
    </TableWrapper>
  );
};
